const PATCHABLE_STATES = new Set(['draft', 'sent', 'failed']);
const MACHINE_MEMO_STATES = new Set(['draft', 'sent', 'failed']);
const RETRY_STATES = new Set(['sending']);
const MACHINE_KINDS = new Set(['connected', 'manual']);

export const effectiveDispatchState = (order = {}) => {
  const explicit = String(order.dispatchState || '').trim().toLowerCase();
  if (explicit) return explicit;
  return order.machineName ? 'sent' : 'draft';
};

export const machineOptionKey = (machine) => {
  if (!machine?.machineKind || !machine?.machineRef) return '';
  return JSON.stringify([String(machine.machineKind), String(machine.machineRef)]);
};

export const parseMachineOptionKey = (key) => {
  if (!key) return null;
  try {
    const parsed = JSON.parse(key);
    if (!Array.isArray(parsed) || parsed.length !== 2 || !MACHINE_KINDS.has(parsed[0]) || !parsed[1]) return null;
    return { machineKind: String(parsed[0]), machineRef: String(parsed[1]) };
  } catch (_) {
    return null;
  }
};

export const selectedMachineKey = (order = {}) => machineOptionKey({
  machineKind: order.machineKind,
  machineRef: order.machineRef,
});

export const lastSentMachineKey = (order = {}) => machineOptionKey({
  machineKind: order.lastSentMachineKind,
  machineRef: order.lastSentMachineRef,
});

export const isCompleted = (order = {}) => order.status === '완료';

export const canPatch = (order = {}) => !isCompleted(order) && PATCHABLE_STATES.has(effectiveDispatchState(order));
export const canEditPayload = (order = {}) => !isCompleted(order) && effectiveDispatchState(order) === 'draft';
export const canEditMachineMemo = (order = {}) => !isCompleted(order) && MACHINE_MEMO_STATES.has(effectiveDispatchState(order));

export const hasValidSelectedMachine = (order = {}) => Boolean(selectedMachineKey(order));

export const needsResend = (order = {}) => {
  if (order.machineKind === 'manual') return false;
  if (isCompleted(order) || !['sent', 'failed'].includes(effectiveDispatchState(order))) return false;
  const selected = selectedMachineKey(order);
  const applied = lastSentMachineKey(order);
  if (applied) return selected !== applied;
  // Legacy assigned rows have only a name. They are already sent until an immutable
  // selection is introduced; that transition must be resent to establish its route.
  return Boolean(selected);
};

export const canSend = (order = {}) => {
  if (isCompleted(order) || order.machineKind === 'manual' || !hasValidSelectedMachine(order)) return false;
  const state = effectiveDispatchState(order);
  return PATCHABLE_STATES.has(state) || RETRY_STATES.has(state);
};

const hasPriorSendEvidence = (order = {}) => Number(order.sendCount || 0) > 0
  && Boolean(order.lastSentMachineRef || order.lastSentMachineName);

export const canComplete = (order = {}) => {
  if (isCompleted(order)) return false;
  if (order.machineKind === 'manual') return hasValidSelectedMachine(order);
  const state = effectiveDispatchState(order);
  if (state === 'sent') return true;
  return ['failed', 'sending'].includes(state) && hasPriorSendEvidence(order);
};

export const dispatchLabel = (order = {}) => {
  if (isCompleted(order)) return '완료';
  return ({ draft: '초안', sending: '전송 중', sent: '전송됨', failed: '전송 실패', unknown: '결과 미확인' })[effectiveDispatchState(order)] || '상태 미확인';
};

export const isUnknownAttemptState = (order = {}) => RETRY_STATES.has(effectiveDispatchState(order));
